/**
 * Searchable Dropdown Component
 * 
 * A reusable dropdown component with search functionality.
 * Supports both single-select and multi-select modes.
 */

import { SEARCH_DEBOUNCE_MS, BLUR_DELAY_MS, MIN_SEARCH_CHARS, CSS_CLASSES, ICONS } from '../wizard-constants.js';
import { t } from '../../../i18n.js';

export class SearchableDropdown {
  constructor(config) {
    // Required config
    this.placeholder = config.placeholder || 'Search...';
    this.onSelect = config.onSelect; // (item) => void
    this.renderItem = config.renderItem; // (item, isSelected) => HTMLElement or { text, details }
    
    // Optional config
    this.onSearch = config.onSearch || null; // async (query) => items[]
    this.items = config.items || []; // Static items if no search function
    this.multiSelect = config.multiSelect || false;
    this.showIcon = config.showIcon !== false;
    this.icon = config.icon || ICONS.SEARCH;
    this.showToggleIcon = config.showToggleIcon !== false; // Show dropdown toggle icon (▼/▲)
    this.isSelected = config.isSelected || ((item) => false); // (item) => boolean
    this.debounceMs = config.debounceMs || SEARCH_DEBOUNCE_MS;
    this.additionalClasses = config.additionalClasses || {};
    this.closeOnSelect = config.closeOnSelect !== undefined ? config.closeOnSelect : !this.multiSelect;
    this.showNoneOption = config.showNoneOption || false;
    this.onNoneSelected = config.onNoneSelected || null;
    
    // Internal state
    this.searchTimeout = null;
    this.searchResults = [];
    this.currentQuery = '';
    this.abortController = null; // For canceling pending requests
  }

  render() {
    const container = document.createElement('div');
    container.className = this.additionalClasses.container || 'dropdown-input-container';

    // Label (optional)
    if (this.label) {
      const label = document.createElement('label');
      label.className = 'dropdown-label';
      label.textContent = this.label;
      container.appendChild(label);
    }

    const inputWrapper = document.createElement('div');
    inputWrapper.className = this.additionalClasses.wrapper || CSS_CLASSES.DROPDOWN_INPUT_WRAPPER;

    // Input
    const input = document.createElement('input');
    input.type = 'text';
    input.className = this.additionalClasses.input || CSS_CLASSES.DROPDOWN_INPUT;
    input.placeholder = this.placeholder;
    input.autocomplete = 'off';

    // Icon (left side - search icon)
    let iconElement = null;
    if (this.showIcon) {
      iconElement = document.createElement('span');
      iconElement.className = this.additionalClasses.icon || CSS_CLASSES.SEARCH_ICON;
      iconElement.innerHTML = this.icon;
    }

    // Toggle icon (right side - dropdown arrow)
    let toggleIcon = null;
    if (this.showToggleIcon) {
      toggleIcon = document.createElement('span');
      toggleIcon.className = this.additionalClasses.toggleIcon || CSS_CLASSES.DROPDOWN_ICON;
      toggleIcon.innerHTML = ICONS.DROPDOWN_CLOSED;
      toggleIcon.style.cursor = 'pointer';
    }

    // Dropdown
    const dropdown = document.createElement('div');
    dropdown.className = this.additionalClasses.dropdown || CSS_CLASSES.DROPDOWN_MENU;
    dropdown.style.display = 'none';

    // Setup event handlers
    this.setupInputHandlers(input, dropdown, toggleIcon);
    this.setupDropdownHandlers(container, dropdown);
    if (toggleIcon) {
      this.setupToggleIconHandlers(toggleIcon, input, dropdown);
    }

    // Build DOM
    if (iconElement) inputWrapper.appendChild(iconElement);
    inputWrapper.appendChild(input);
    if (toggleIcon) inputWrapper.appendChild(toggleIcon);
    inputWrapper.appendChild(dropdown);
    container.appendChild(inputWrapper);

    // Store references
    this.container = container;
    this.input = input;
    this.dropdown = dropdown;

    return container;
  }

  setupInputHandlers(input, dropdown, toggleIcon) {
    // Input change handler
    input.addEventListener('input', async (e) => {
      const query = e.target.value.trim();
      this.currentQuery = query;

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Cancel any pending request
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      if (query.length === 0) {
        if (this.onSearch) {
          dropdown.style.display = 'none';
          this.searchResults = [];
        } else {
          // Show all items for static dropdowns
          this.renderResults(this.items, dropdown);
          dropdown.style.display = 'block';
        }
        return;
      }

      // Check minimum character requirement for search operations
      if (this.onSearch && query.length < MIN_SEARCH_CHARS) {
        dropdown.innerHTML = `<div class="${CSS_CLASSES.DROPDOWN_ITEM} ${CSS_CLASSES.DROPDOWN_ITEM_EMPTY}">${t('dropdown_min_chars', { n: String(MIN_SEARCH_CHARS) })}</div>`;
        dropdown.style.display = 'block';
        return;
      }

      // Show loading
      dropdown.innerHTML = `<div class="${CSS_CLASSES.DROPDOWN_ITEM} ${CSS_CLASSES.DROPDOWN_ITEM_EMPTY}">${t('dropdown_searching')}</div>`;
      dropdown.style.display = 'block';

      // Debounce search
      this.searchTimeout = setTimeout(async () => {
        await this.performSearch(query, dropdown);
      }, this.debounceMs);
    });

    // Focus handler
    input.addEventListener('focus', () => {
      this.closeOtherDropdowns(dropdown);
      
      if (this.onSearch) {
        if (input.value.trim().length > 0 && this.searchResults.length > 0) {
          dropdown.style.display = 'block';
          this.renderResults(this.searchResults, dropdown);
          if (toggleIcon) toggleIcon.innerHTML = ICONS.DROPDOWN_OPEN;
        }
      } else {
        // Show all items for static dropdowns
        this.renderResults(this.items, dropdown);
        dropdown.style.display = 'block';
        if (toggleIcon) toggleIcon.innerHTML = ICONS.DROPDOWN_OPEN;
      }
    });

    // Keyboard handlers
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        dropdown.style.display = 'none';
        input.blur();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const firstItem = dropdown.querySelector(`.${CSS_CLASSES.DROPDOWN_ITEM}:not(.${CSS_CLASSES.DROPDOWN_ITEM_EMPTY})`);
        if (firstItem) {
          firstItem.click();
        }
      }
    });

    // Blur handler
    if (!this.multiSelect) {
      input.addEventListener('blur', () => {
        setTimeout(() => {
          // Clear invalid input if needed
          if (!this.multiSelect && input.value.trim() && !this.validateInput(input.value)) {
            input.value = '';
          }
        }, BLUR_DELAY_MS);
      });
    }
  }

  setupDropdownHandlers(container, dropdown) {
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        dropdown.style.display = 'none';
        const toggleIcon = container.querySelector(`.${CSS_CLASSES.DROPDOWN_ICON}`);
        if (toggleIcon) toggleIcon.innerHTML = ICONS.DROPDOWN_CLOSED;
      }
    });
  }

  setupToggleIconHandlers(toggleIcon, input, dropdown) {
    toggleIcon.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.style.display === 'block';
      
      // Close all other dropdowns
      this.closeOtherDropdowns(dropdown);
      
      if (isOpen) {
        // Close this dropdown
        dropdown.style.display = 'none';
        toggleIcon.innerHTML = ICONS.DROPDOWN_CLOSED;
      } else {
        // Open this dropdown
        dropdown.style.display = 'block';
        toggleIcon.innerHTML = ICONS.DROPDOWN_OPEN;
        input.focus();
        
        // Render items if static dropdown
        if (!this.onSearch) {
          this.renderResults(this.items, dropdown);
        }
      }
    });
  }

  async performSearch(query, dropdown) {
    try {
      let items;
      
      if (this.onSearch) {
        // Create new abort controller for this request
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        
        try {
          items = await this.onSearch(query, signal);
        } catch (error) {
          // Check if the request was aborted (user kept typing)
          if (error.name === 'AbortError') {
            console.log('Search request canceled:', query);
            return; // Don't show error, just return
          }
          throw error; // Re-throw other errors
        }
      } else {
        // Filter static items
        items = this.items.filter(item => {
          const searchText = this.getItemSearchText(item).toLowerCase();
          return searchText.includes(query.toLowerCase());
        });
      }

      this.searchResults = items;
      this.renderResults(items, dropdown);
    } catch (error) {
      console.error('Search failed:', error);
      dropdown.innerHTML = `<div class="${CSS_CLASSES.DROPDOWN_ITEM} ${CSS_CLASSES.DROPDOWN_ITEM_EMPTY} error">${t('dropdown_search_failed', { message: error.message })}</div>`;
    }
  }

  renderResults(items, dropdown) {
    dropdown.innerHTML = '';

    if (items.length === 0) {
      const emptyItem = document.createElement('div');
      emptyItem.className = `${CSS_CLASSES.DROPDOWN_ITEM} ${CSS_CLASSES.DROPDOWN_ITEM_EMPTY}`;
      emptyItem.textContent = this.currentQuery 
        ? t('dropdown_no_results', { q: this.currentQuery })
        : t('dropdown_type_to_search');
      dropdown.appendChild(emptyItem);
      return;
    }

    // Add "None" option if configured
    if (this.showNoneOption && !this.currentQuery) {
      const noneItem = document.createElement('div');
      noneItem.className = CSS_CLASSES.DROPDOWN_ITEM;
      noneItem.textContent = t('dropdown_none');
      noneItem.onclick = () => {
        if (this.onNoneSelected) {
          this.onNoneSelected();
        }
        if (this.closeOnSelect) {
          dropdown.style.display = 'none';
        }
        this.input.value = '';
      };
      dropdown.appendChild(noneItem);
    }

    // Render items
    items.forEach(item => {
      const dropdownItem = document.createElement('div');
      dropdownItem.className = CSS_CLASSES.DROPDOWN_ITEM;

      const isSelected = this.isSelected(item);
      if (isSelected) {
        dropdownItem.classList.add(CSS_CLASSES.DROPDOWN_ITEM_SELECTED);
      }

      // Render item content
      const rendered = this.renderItem(item, isSelected);
      if (rendered instanceof HTMLElement) {
        dropdownItem.appendChild(rendered);
      } else if (typeof rendered === 'object') {
        // Simple text/details rendering
        const info = document.createElement('div');
        info.className = 'item-info';
        
        const title = document.createElement('div');
        title.className = 'item-title';
        title.textContent = rendered.text || t('dropdown_unknown');
        info.appendChild(title);
        
        if (rendered.details) {
          const details = document.createElement('div');
          details.className = 'item-details';
          details.textContent = rendered.details;
          info.appendChild(details);
        }
        
        dropdownItem.appendChild(info);
        
        if (isSelected && this.multiSelect) {
          const checkmark = document.createElement('span');
          checkmark.className = CSS_CLASSES.CHECKMARK;
          checkmark.innerHTML = ICONS.CHECKMARK;
          dropdownItem.appendChild(checkmark);
        }
      } else {
        // Simple string
        dropdownItem.textContent = rendered;
      }

      // Click handler
      dropdownItem.onclick = () => {
        this.handleItemSelect(item, dropdown);
      };

      dropdown.appendChild(dropdownItem);
    });
  }

  handleItemSelect(item, dropdown) {
    if (this.onSelect) {
      this.onSelect(item);
    }

    if (!this.multiSelect) {
      this.input.value = this.getItemDisplayText(item);
    }

    if (this.closeOnSelect) {
      dropdown.style.display = 'none';
      if (!this.multiSelect) {
        this.input.blur();
      }
    } else {
      // Re-render to update checkmarks
      const items = this.onSearch ? this.searchResults : this.items;
      this.renderResults(items, dropdown);
    }
  }

  getItemSearchText(item) {
    if (typeof item === 'string') return item;
    return item.value || item.name || item.text || '';
  }

  getItemDisplayText(item) {
    if (typeof item === 'string') return item;
    return item.value || item.name || item.text || t('dropdown_unknown');
  }

  validateInput(value) {
    // Override this in subclasses if needed
    return true;
  }

  closeOtherDropdowns(currentDropdown) {
    document.querySelectorAll(`.${CSS_CLASSES.DROPDOWN_MENU}`).forEach(menu => {
      if (menu !== currentDropdown) {
        menu.style.display = 'none';
      }
    });
    // Reset all toggle icons
    document.querySelectorAll(`.${CSS_CLASSES.DROPDOWN_ICON}`).forEach(icon => {
      icon.innerHTML = ICONS.DROPDOWN_CLOSED;
    });
  }

  // Public methods for external control
  clear() {
    if (this.input) {
      this.input.value = '';
    }
    if (this.dropdown) {
      this.dropdown.style.display = 'none';
    }
    this.searchResults = [];
    this.currentQuery = '';
  }

  setValue(text) {
    if (this.input) {
      this.input.value = text;
    }
  }

  refresh(items) {
    this.items = items || this.items;
    if (this.dropdown && this.dropdown.style.display === 'block') {
      this.renderResults(this.items, this.dropdown);
    }
  }
}

